import React from "react"

import type { Organization } from "@/models/organization"
import { useAuthorizedOrganizations } from "@/services/organization"

interface AuthorizationContextValue {
  authorizedOrganizations: Organization[]
  authorizedOrganizationsError: unknown
  authorizedOrganizationsIsError: boolean
  authorizedOrganizationsIsFetched: boolean
  authorizedOrganizationsIsFetching: boolean
  refetchAuthorizedOrganizations: () => Promise<unknown>
}

interface AuthorizationProviderProps extends Partial<AuthorizationContextValue> {
  children: React.ReactNode
}

const AuthorizationContext = React.createContext<AuthorizationContextValue>({
  authorizedOrganizations: [],
  authorizedOrganizationsError: null,
  authorizedOrganizationsIsError: false,
  authorizedOrganizationsIsFetched: false,
  authorizedOrganizationsIsFetching: false,
  refetchAuthorizedOrganizations: () => Promise.resolve(undefined),
})

/**
 * Provides the authorization context
 *
 * @example
 * return (
 *   <AuthorizationProvider>
 *     <Component />
 *   </AuthorizationProvider>
 * )
 */
export const AuthorizationProvider: React.FC<AuthorizationProviderProps> = (
  props
) => {
  const {
    authorizedOrganizationsData,
    authorizedOrganizationsError,
    authorizedOrganizationsIsError,
    authorizedOrganizationsIsFetched,
    authorizedOrganizationsIsFetching,
    authorizedOrganizationsRefetch,
  } = useAuthorizedOrganizations()

  const value: AuthorizationContextValue = React.useMemo(
    () => ({
      authorizedOrganizations: authorizedOrganizationsData ?? [],
      authorizedOrganizationsError,
      authorizedOrganizationsIsError,
      authorizedOrganizationsIsFetched,
      authorizedOrganizationsIsFetching,
      refetchAuthorizedOrganizations: authorizedOrganizationsRefetch,
    }),
    [
      authorizedOrganizationsData,
      authorizedOrganizationsError,
      authorizedOrganizationsIsError,
      authorizedOrganizationsIsFetched,
      authorizedOrganizationsIsFetching,
      authorizedOrganizationsRefetch,
    ]
  )

  return (
    <AuthorizationContext value={value}>{props.children}</AuthorizationContext>
  )
}

/**
 * Provides authorization context
 *
 * @example
 * const { authorizedOrganizations } = useAuthorizationContext()
 */
export const useAuthorization = () => React.use(AuthorizationContext)
